import React from "react";
import type { LandmarkArtName } from "../../types/courseTheme";
import { cableY, crenels, horseshoe, pointedArch, ring, roundArch, spire, voussoirs, waves } from "../../utils/svgPaths";
interface LandmarkArtProps {
  name: LandmarkArtName;
  /** Accent fills (already include alpha). */
  a: string;
  b: string;
  className?: string;
}

/** Architectural line drawings on a 200×200 grid, standing on a ground line near y=190. */
export function LandmarkArt({
  name,
  a,
  b,
  className
}: LandmarkArtProps) {
  return <svg viewBox="0 0 200 200" fill="none" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true" data-id="element-563" data-mp="3m1ubpfptenru">
      {art[name](a, b)}
    </svg>;
}
function Windmill({
  a,
  b
}: {
  a: string;
  b: string;
}) {
  return <>
      <path d="M82 190 L88 112 H112 L118 190 Z" fill={a} data-id="element-564" data-mp="1fa1tu07h9aec" />
      <path d="M86 112 L100 94 L114 112 Z" fill={b} data-id="element-565" data-mp="1aemibewxzql3" />
      <path d={roundArch(100, 190, 176, 5)} data-id="element-566" data-mp="2tmrpg7wpv41x" />
      <path d="M97 140 h6 v8 h-6 Z" data-id="element-567" data-mp="2tlyq3s037gtt" />
      <g className="lm-spin" data-id="element-568" data-mp="2tp4et64t0gp0">
        {[15, 105, 195, 285].map(r => <g key={r} transform={`rotate(${r} 100 106)`} data-id="element-569" data-mp="2ynfnven9yn66">
            <path d="M100 106 V40" data-id="element-570" data-mp="2jp3pn3k976cw" />
            <path d="M100 48 H112 V96 H100 M100 64 H112 M100 80 H112 M106 48 V96" data-id="element-571" data-mp="2jq55dlc7sh0y" />
          </g>)}
      </g>
      <circle cx="100" cy="106" r="3" fill={b} data-id="element-572" data-mp="2oprgnp8hfn74" />
    </>;
}
const art: Record<LandmarkArtName, (a: string, b: string) => React.ReactNode> = {
  /* ---------------- Spain ---------------- */
  sagrada: (a, b) => {
    const spires: [number, number, number][] = [[58, 52, 8], [78, 24, 9], [100, 8, 11], [122, 24, 9], [142, 52, 8]];
    return <>
        <path d="M36 190 V122 H164 V190" fill={a} data-id="element-573" data-mp="37kpx6ugj828g" />
        {spires.map(([c, t, w]) => <path key={c} d={spire(c, t, w, 122)} fill={b} data-id="element-574" data-mp="3vaspnbocza7l" />)}
        {spires.map(([c, t]) => <path key={`x${c}`} d={`M${c} ${t} v-6 M${c - 3} ${t - 3} h6 M${c} ${t + 44} v6 M${c} ${t + 58} v6`} data-id="element-575" data-mp="3yr0rk5px7r0" />)}
        <circle cx="100" cy="140" r="9" data-id="element-576" data-mp="8ubvff40dgga" />
        <path d="M100 131 V149 M91 140 H109" data-id="element-577" data-mp="8vekrilonau3" />
        <path d={pointedArch(100, 190, 176, 11)} data-id="element-578" data-mp="8simft5xak54" />
        <path d={pointedArch(64, 190, 178, 8)} data-id="element-579" data-mp="4rozb20b19da" />
        <path d={pointedArch(136, 190, 178, 8)} data-id="element-580" data-mp="4ukwup88pw17" />
        <path d="M20 190 H180" data-id="element-581" data-mp="3vxzuwimjphx8" />
      </>;
  },
  alhambra: (a, b) => <>
      <path d="M0 112 L28 84 L46 98 L78 62 L108 92 L138 72 L168 94 L200 80" strokeDasharray="3 4" data-id="element-582" data-mp="3r0hqn5jh3qvk" />
      <path d="M70 70 L78 62 L86 70 M130 80 L138 72 L146 80" data-id="element-583" data-mp="3m8zssqcqy6et" />
      <path d="M0 190 C40 164 78 154 108 154 C146 154 178 166 200 190" fill={b} data-id="element-584" data-mp="3m7ewav6vgdeb" />
      <path d="M30 154 V132 H172 V156" fill={a} data-id="element-585" data-mp="3haer0mhujeo9" />
      <path d={crenels(58, 108, 132, 5)} data-id="element-586" data-mp="3d937mypkfpoi" />
      <path d={crenels(140, 172, 132, 5)} data-id="element-587" data-mp="3daeex8i7zfkd" />
      <path d="M30 154 V104 H58 V154" fill={a} data-id="element-588" data-mp="38975l50fi8bl" />
      <path d={crenels(30, 58, 104, 4)} data-id="element-589" data-mp="388xgdjn7k56y" />
      <path d="M108 154 V94 H140 V154" fill={a} data-id="element-590" data-mp="33anih1a3mqhn" />
      <path d={crenels(108, 140, 94, 4)} data-id="element-591" data-mp="33avwiwhyk1uf" />
      <path d={roundArch(44, 124, 118, 4)} data-id="element-592" data-mp="1isbl19ny3sjv" />
      <path d={roundArch(118, 116, 110, 3)} data-id="element-593" data-mp="1irjp1xqfrst2" />
      <path d={roundArch(130, 116, 110, 3)} data-id="element-594" data-mp="1iupifp63lgul" />
      <path d="M160 156 C156 142 158 126 162 118 C166 126 168 142 164 156 Z" fill={a} data-id="element-595" data-mp="1nqd5nz10rzxg" />
      <path d="M18 162 C14 148 16 134 20 126 C24 134 26 148 22 162 Z" fill={a} data-id="element-596" data-mp="1sq9au1lgdxel" />
      <path d="M0 190 H200" data-id="element-597" data-mp="1wrugp2zklbfa" />
    </>,
  windmill: (a, b) => <>
      <g transform="translate(112 95) scale(0.5)" opacity={0.7} data-id="element-598" data-mp="1wpru6sxmm0fb">
        <Windmill a={a} b={b} data-id="element-599" data-mp="10352pnr2d2yl" />
      </g>
      <Windmill a={a} b={b} data-id="element-600" data-mp="150y483hf98sj" />
      <path d="M0 190 H200" data-id="element-601" data-mp="15056fs0fxtmj" />
    </>,
  torreOro: (a, b) => <>
      <path d="M60 182 V102 H140 V182" fill={b} data-id="element-602" data-mp="192jpod80uvec" />
      <path d={crenels(60, 140, 102, 5)} data-id="element-603" data-mp="191qg74hohrsi" />
      <path d="M80 182 V102 M100 182 V102 M120 182 V102" strokeDasharray="2 5" data-id="element-604" data-mp="1dt8f7n055iau" />
      <path d="M76 97 V66 H124 V97" fill={b} data-id="element-605" data-mp="1dvcsgvjxjtns" />
      <path d={crenels(76, 124, 66, 4)} data-id="element-606" data-mp="hc5gguaye790" />
      <path d="M88 62 V46 H112 V62" fill={a} data-id="element-607" data-mp="hb3u3tohlybz" />
      <path d="M88 46 Q100 24 112 46" fill={b} data-id="element-608" data-mp="lcfkw2j3kplr" />
      <path d="M100 35 V30" data-id="element-609" data-mp="lb4j2897prxa" />
      <circle cx="100" cy="28" r="2.2" data-id="element-610" data-mp="28c4epkpq6py" />
      <path d={roundArch(100, 150, 140, 6)} data-id="element-611" data-mp="29dqdp39odmg" />
      <path d={roundArch(90, 86, 80, 3)} data-id="element-612" data-mp="25zo0guls4f0" />
      <path d={roundArch(110, 86, 80, 3)} data-id="element-613" data-mp="3tfexjmk42n2t" />
      <path d={waves(8, 192, 190, 16, 3)} data-id="element-614" data-mp="3tcix9rksjmb7" />
      <path d={waves(32, 168, 197, 16, 2)} data-id="element-615" data-mp="bynmrh558igd" />
    </>,
  alcala: (a, b) => {
    const arches = [64, 100, 136].map(x => `${roundArch(x, 190, 150, 12)} Z`).join(" ");
    return <>
        <path d={`M20 190 V112 H180 V190 Z ${arches}`} fill={a} fillRule="evenodd" data-id="element-616" data-mp="kyamlyi85g7b" />
        <path d="M14 112 H186 V104 H14 Z" data-id="element-617" data-mp="fzqwrn0ifk9o" />
        <path d="M68 104 V84 H132 V104" fill={a} data-id="element-618" data-mp="fyxo2ghzqkow" />
        <path d="M62 84 L100 62 L138 84 Z" fill={b} data-id="element-619" data-mp="uwzssu0rdc7q" />
        {[34, 166].map(x => <path key={x} d={`M${x - 8} 190 V156 H${x + 8} V190`} data-id="element-620" data-mp="uundypqarhyw" />)}
        {[46, 82, 118, 154].map(x => <path key={x} d={`M${x - 3} 112 V190 M${x + 3} 112 V190`} data-id="element-621" data-mp="2wo34znqaqyue" />)}
        <path d="M92 62 V55 M100 62 V50 M108 62 V55" data-id="element-622" data-mp="2wq7jezltng8x" />
        <circle cx="100" cy="46" r="3" data-id="element-623" data-mp="2rql6ysdti416" />
        <path d="M8 190 H192" data-id="element-624" data-mp="2rrmi0wuu2qiw" />
      </>;
  },
  mezquita: a => <>
      <path d="M18 190 V88 H182 V190" data-id="element-625" data-mp="1780vv6npvwr5" />
      <path d={crenels(18, 182, 88, 6)} data-id="element-626" data-mp="1c62ejqh8du23" />
      {[50, 100, 150].map(cx => <g key={cx} data-id="element-627" data-mp="1c852m4ytpd46">
          {voussoirs(cx, 124, 14, 22).map((d, i) => <path key={i} d={d} fill={i % 2 === 0 ? a : "none"} data-id="element-628" data-mp="xh5z38cuherg" data-mpi={i} />)}
          <path d={horseshoe(cx, 124, 22, 190)} data-id="element-629" data-mp="12f7r4is90of2" />
          <path d={`M${cx - 22} 136 h6 M${cx + 16} 136 h6`} data-id="element-630" data-mp="ogrcm5o59tmd" />
        </g>)}
    </>,
  /* ---------------- France ---------------- */
  eiffel: (a, b) => <>
      <path d="M100 6 V20" data-id="element-631" data-mp="sjnvzylidtrd" />
      <path d="M94 20 H106 L110 60 H90 Z" fill={b} data-id="element-632" data-mp="eijzjxwz7vpn" />
      <path d="M88 60 H112 L120 110 H80 Z" fill={a} data-id="element-633" data-mp="ek4qldk30wir" />
      <path d="M76 110 H124 L150 190 H132 C124 160 76 160 68 190 H50 Z" fill={a} data-id="element-634" data-mp="jhes3rkqwymy" />
      <path d="M92 30 L108 50 M108 30 L92 50 M84 70 L116 100 M116 70 L84 100 M74 124 L126 150 M126 124 L74 150" strokeOpacity={0.6} data-id="element-635" data-mp="28wl5f2qy1iz8" />
      <path d="M72 110 H128 M86 60 H114" data-id="element-636" data-mp="2dvxt1ppn2u2v" />
      <path d="M30 190 H170" data-id="element-637" data-mp="2dtlc9fvmapre" />
    </>,
  arc: a => <>
      <path d={`M50 190 V74 H150 V190 Z ${roundArch(100, 190, 132, 22)} Z`} fill={a} fillRule="evenodd" data-id="element-638" data-mp="2cfk1ey9bvoze" />
      <path d="M44 74 H156 V60 H44 Z" data-id="element-639" data-mp="27emkep0210zh" />
      <path d="M50 88 H150" data-id="element-640" data-mp="27hs6rwhatori" />
      <path d="M56 67 H144" strokeDasharray="3 3" data-id="element-641" data-mp="22ft64ucy15y9" />
      <path d="M60 136 h16 v28 h-16 Z M124 136 h16 v28 h-16 Z" data-id="element-642" data-mp="22if9ehsswzao" />
      <path d="M20 190 H180" data-id="element-643" data-mp="1xib3flpirmyp" />
    </>,
  notreDame: (a, b) => <>
      <path d="M100 96 V28 M96 96 L100 28 L104 96" data-id="element-644" data-mp="2v5jhwwr3rvr7" />
      <path d="M40 190 V58 H80 V190 Z M120 190 V58 H160 V190 Z" fill={a} data-id="element-645" data-mp="2v48j7bcile6y" />
      <path d="M80 190 V96 H120 V190" fill={b} data-id="element-646" data-mp="2q7882m0t5l8z" />
      <path d="M36 58 H84 M116 58 H164" data-id="element-647" data-mp="2lf703caoagor" />
      {[52, 68, 132, 148].map(x => <path key={x} d={pointedArch(x, 100, 82, 4)} data-id="element-648" data-mp="2ld44mcqr3vcn" />)}
      <circle cx="100" cy="118" r="13" data-id="element-649" data-mp="2gfc9zayq1v4r" />
      {ring(100, 118, 13, 8).map(([x, y], i) => <path key={i} d={`M100 118 L${x} ${y}`} data-id="element-650" data-mp="3dwjccdhd9xrv" data-mpi={i} />)}
      <path d="M40 142 H160" strokeDasharray="2 4" data-id="element-651" data-mp="394rnq8hru24d" />
      <path d={pointedArch(60, 190, 170, 9)} data-id="element-652" data-mp="393zrqwk9i2dk" />
      <path d={pointedArch(100, 190, 170, 11)} data-id="element-653" data-mp="3hdf9r0kj92bp" />
      <path d={pointedArch(140, 190, 170, 9)} data-id="element-654" data-mp="3heqk5j8hh8bs" />
      <path d="M20 190 H180" data-id="element-655" data-mp="3lf0u16zecivx" />
    </>,
  /* ---------------- Germany ---------------- */
  brandenburg: (a, b) => <>
      <path d="M10 190 V140 H30 M170 140 H190 V190" data-id="element-656" data-mp="3qdu4eqjdztrv" />
      <path d="M30 92 H170 V108 H30 Z" fill={a} data-id="element-657" data-mp="3qggd4ti0kfbo" />
      {[38, 62, 86, 114, 138, 162].map(x => <path key={x} d={`M${x - 4} 108 V190 M${x + 4} 108 V190`} data-id="element-658" data-mp="3vau1iby9dm6u" />)}
      <path d="M70 92 V78 H130 V92" fill={b} data-id="element-659" data-mp="46dq27fgt1px" />
      <path d="M84 78 C84 70 90 66 94 70 C96 62 104 62 106 70 C110 66 116 70 116 78" fill={a} data-id="element-660" data-mp="888tpikvbr6v" />
      <path d="M100 64 V52 M95 56 H105" data-id="element-661" data-mp="czh3wgu0vgl3" />
      <circle cx="100" cy="48" r="3" data-id="element-662" data-mp="d0id0frh9t06" />
      <path d="M4 190 H196" data-id="element-663" data-mp="d1jryvbm6zn6" />
    </>,
  neuschwanstein: (a, b) => <>
      <path d="M16 190 C28 176 40 172 58 170 H152 C168 172 178 180 186 190" data-id="element-664" data-mp="hxinr3z9vjxm" />
      <path d="M50 170 V88 H66 V170" fill={a} data-id="element-665" data-mp="myoc6plwgl1c" />
      <path d="M46 88 L58 62 L70 88 Z" fill={b} data-id="element-666" data-mp="rwhlvsmevx6d" />
      <path d="M66 170 V100 H132 V170" fill={a} data-id="element-667" data-mp="rx9hh3g0duwn" />
      <path d="M78 100 L96 78 L114 100" fill={b} data-id="element-668" data-mp="1d7esdvbw5psn" />
      <path d="M114 100 V80 H124 V100" data-id="element-669" data-mp="1d5v87tn46ull" />
      <path d="M112 80 L119 64 L126 80 Z" fill={b} data-id="element-670" data-mp="188bvwz9z8rwa" />
      <path d="M136 170 V56 H156 V170" fill={a} data-id="element-671" data-mp="187jz5l4n8o4f" />
      <path d="M132 56 L146 22 L160 56 Z" fill={b} data-id="element-672" data-mp="1m84f89j6c5u8" />
      <path d="M146 22 V13 L153 15.5 L146 18" data-id="element-673" data-mp="1m7kygw59fnhs" />
      {[74, 86, 98, 110, 122].flatMap(x => [116, 138].map(y => <path key={`${x}-${y}`} d={roundArch(x, y + 10, y + 4, 3)} data-id="element-674" data-mp="1h9tauebnenje" />))}
      <path d={roundArch(146, 90, 84, 3)} data-id="element-675" data-mp="tk8p5no41c6f" />
      <path d={roundArch(146, 124, 118, 3)} data-id="element-676" data-mp="phu5x2gj4aem" />
      <path d={roundArch(58, 110, 104, 3)} data-id="element-677" data-mp="pivkhgwr7f13" />
      <path d="M0 190 H200" data-id="element-678" data-mp="pg0xpiq9pi4i" />
    </>,
  kolnerDom: (a, b) => <>
      <path d="M40 190 V122 H160 V190" fill={a} data-id="element-679" data-mp="13ajsb7zyzhc9" />
      <path d={spire(66, 10, 18, 122)} fill={b} data-id="element-680" data-mp="yi0fid93wq8i" />
      <path d={spire(134, 10, 18, 122)} fill={b} data-id="element-681" data-mp="yh7fdv4nkyzc" />
      <path d="M66 10 v-6 M134 10 v-6 M66 62 v8 M66 78 v8 M66 94 v8 M134 62 v8 M134 78 v8 M134 94 v8" data-id="element-682" data-mp="asfsnj9u18ty" />
      <path d="M84 122 L100 100 L116 122" data-id="element-683" data-mp="5umpz07qmwyf" />
      <circle cx="100" cy="142" r="7" data-id="element-684" data-mp="5tl2txdg6k0c" />
      <path d={pointedArch(66, 190, 172, 8)} data-id="element-685" data-mp="2hhggahron8hd" />
      <path d={pointedArch(100, 190, 172, 10)} data-id="element-686" data-mp="2hfna13kzeq9w" />
      <path d={pointedArch(134, 190, 172, 8)} data-id="element-687" data-mp="2m7oi0db49uu4" />
      <path d="M20 190 H180" data-id="element-688" data-mp="2m9hpfutk0j36" />
    </>,
  /* ---------------- Italy ---------------- */
  colosseo: a => <>
      <path d="M16 190 V76 Q70 64 120 64 L136 88 L152 96 L166 110 L184 116 V190 Z" fill={a} data-id="element-689" data-mp="28efcinqahtgv" />
      <path d="M16 106 H150 M16 140 H184 M16 170 H184" data-id="element-690" data-mp="2ddih672cxxob" />
      {[28, 46, 64, 82, 100, 118, 136, 154, 172].map(x => <g key={x} data-id="element-691" data-mp="356smr9vml2fz">
          <path d={roundArch(x, 140, 124, 6)} data-id="element-692" data-mp="357ar6tsfymyz" />
          <path d={roundArch(x, 170, 156, 6)} data-id="element-693" data-mp="355zoyyen9n9z" />
          {x <= 136 && <path d={roundArch(x, 106, 92, 5)} data-id="element-694" data-mp="2r7swxywdmhi8" />}
          {x <= 112 && <path d={`M${x - 3} 82 v-6 h6 v6`} data-id="element-695" data-mp="2r9dr3nes3yfk" />}
        </g>)}
      <path d="M8 190 H192" data-id="element-696" data-mp="2w4jeyrrqrn6a" />
    </>,
  pisa: (a, b) => <>
      <g transform="rotate(5 100 190)" data-id="element-697" data-mp="1frl2kkr3mchn">
        <path d="M78 190 V40 H122 V190" fill={a} data-id="element-698" data-mp="1fqjjpu3uclpd" />
        <path d="M84 40 V22 H116 V40" fill={b} data-id="element-699" data-mp="1kr7i50ueg4tm" />
        <path d={roundArch(94, 40, 32, 3)} data-id="element-700" data-mp="1kouqfvyuag3d" />
        <path d={roundArch(106, 40, 32, 3)} data-id="element-701" data-mp="zg10y1kxytk2" />
        {[60, 80, 100, 120, 140, 160].map(y => <path key={y} d={`M76 ${y} H124`} data-id="element-702" data-mp="14g5g9oa45i8p" />)}
        {[40, 60, 80, 100, 120, 140].flatMap(y => [86, 100, 114].map(x => <path key={`${x}-${y}`} d={roundArch(x, y + 20, y + 10, 4)} data-id="element-703" data-mp="pq7zhteijutj" />))}
        <path d="M74 190 H126" data-id="element-704" data-mp="uosr54fp966d" />
      </g>
      <path d="M40 190 H160" data-id="element-705" data-mp="unrfoyuruho4" />
    </>,
  duomo: (a, b) => <>
      <path d="M30 190 V150 H170 V190" fill={a} data-id="element-706" data-mp="gohxjtn09dhi" />
      <path d="M44 150 V130 H156 V150" fill={b} data-id="element-707" data-mp="krem00z883td" />
      {[64, 100, 136].map(x => <circle key={x} cx={x} cy="140" r="4" data-id="element-708" data-mp="6rvdh72i6qgj" />)}
      <path d="M48 130 C48 74 152 74 152 130" fill={a} data-id="element-709" data-mp="6t6ptva0l6j9" />
      <path d="M100 88 V130 M76 94 Q70 110 68 130 M124 94 Q130 110 132 130" data-id="element-710" data-mp="bqq3ou2svhao" />
      <path d="M93 88 V74 H107 V88" fill={b} data-id="element-711" data-mp="bst50a9ekwup" />
      <path d="M93 74 L100 60 L107 74 M100 60 V54" data-id="element-712" data-mp="216xyoskirmdn" />
      <path d={roundArch(60, 190, 172, 7)} data-id="element-713" data-mp="2143gl7ozadne" />
      <path d={roundArch(100, 190, 170, 9)} data-id="element-714" data-mp="263y4d2dgin48" />
      <path d={roundArch(140, 190, 172, 7)} data-id="element-715" data-mp="262n256zntnf8" />
      <path d="M16 190 H184" data-id="element-716" data-mp="3qm8qn3u90ta5" />
    </>,
  /* ---------------- United Kingdom ---------------- */
  bigBen: (a, b) => <>
      <path d="M30 190 V150 H84 M116 150 H170 V190" data-id="element-717" data-mp="3lpi6a4bevbix" />
      <path d="M36 164 H78 M122 164 H164" strokeDasharray="3 4" data-id="element-718" data-mp="47z30wy57paf" />
      <path d="M84 190 V110 H116 V190" fill={a} data-id="element-719" data-mp="49a58sbxwozf" />
      <path d="M92 190 V110 M100 190 V110 M108 190 V110" strokeDasharray="2 5" data-id="element-720" data-mp="3vl3g2omq1g38" />
      <path d="M80 110 V70 H120 V110 Z" fill={b} data-id="element-721" data-mp="d7m2veb84n1d" />
      <circle cx="100" cy="90" r="13" data-id="element-722" data-mp="d6aqwr7mk8z6" />
      <path d="M100 90 V81 M100 90 L106 94" data-id="element-723" data-mp="89arv09706hk" />
      <path d="M84 70 V52 H116 V70" data-id="element-724" data-mp="88iw9pfli8ra" />
      <path d={roundArch(93, 68, 60, 3)} data-id="element-725" data-mp="n6bame9eor3w" />
      <path d={roundArch(107, 68, 60, 3)} data-id="element-726" data-mp="n5a0cc07s8n8" />
      <path d="M82 52 L100 10 L118 52 Z" fill={a} data-id="element-727" data-mp="n3yxceelf4x6" />
      <path d="M84 52 V44 M116 52 V44 M100 10 V4" data-id="element-728" data-mp="i89vg4f3pbxp" />
      <path d="M20 190 H180" data-id="element-729" data-mp="i5ds20khhv1v" />
    </>,
  towerBridge: (a, b) => <>
      <path d="M0 104 Q24 146 40 150 M160 150 Q176 146 200 104" data-id="element-730" data-mp="2oxy6at48ta6p" />
      <path d="M0 150 H40 M70 150 H130 M160 150 H200" data-id="element-731" data-mp="2jzmvaf27otmw" />
      <path d="M70 64 H130 M70 74 H130 M70 64 L80 74 L90 64 L100 74 L110 64 L120 74 L130 64" data-id="element-732" data-mp="39mqogbxvw13n" />
      {[55, 145].map(x => <g key={x} data-id="element-733" data-mp="3dnssd62qx1by">
          <path d={`M${x - 15} 190 V56 H${x + 15} V190`} fill={a} data-id="element-734" data-mp="3dp3tey4t3uzd" />
          <path d={`M${x - 17} 56 L${x} 32 L${x + 17} 56 Z`} fill={b} data-id="element-735" data-mp="3imvw8n6zp1ic" />
          <path d={`M${x - 15} 56 V46 M${x + 15} 56 V46 M${x} 32 V26`} data-id="element-736" data-mp="3ipruydqnvu7u" />
          <path d={pointedArch(x, 100, 88, 5)} data-id="element-737" data-mp="3no338k1b5wny" />
          <path d={pointedArch(x, 150, 136, 6)} data-id="element-738" data-mp="3slusf6akj4og" />
        </g>)}
      <path d={waves(0, 200, 184, 16, 3)} data-id="element-739" data-mp="3sjifj7jo3ki9" />
      <path d={waves(12, 188, 192, 16, 2)} data-id="element-740" data-mp="h0r9fge85lu" />
    </>,
  londonEye: a => <>
      <path d="M100 90 L72 190 M100 90 L84 190" data-id="element-741" data-mp="580mmg9v6fm8" />
      <g className="lm-spin-slow" data-id="element-742" data-mp="59ao7ffxe5xm">
        <circle cx="100" cy="90" r="70" data-id="element-743" data-mp="a7lvbieu62c5" />
        <circle cx="100" cy="90" r="64" strokeOpacity={0.5} data-id="element-744" data-mp="a6aiyu7brm9f" />
        <circle cx="100" cy="90" r="6" data-id="element-745" data-mp="f6ylod4x1rjh" />
        {ring(100, 90, 64, 16).map(([x, y], i) => <path key={i} d={`M100 90 L${x} ${y}`} strokeOpacity={0.5} data-id="element-746" data-mp="k5jlibg9a97e" data-mpi={i} />)}
        {ring(100, 90, 70, 16).map(([x, y], i) => <circle key={`p${i}`} cx={x} cy={y} r="3.5" fill={a} data-id="element-747" data-mp="24ovde1vao9nn" data-mpi={i} />)}
      </g>
      <path d="M40 190 H160" data-id="element-748" data-mp="1zohhtjavqs6i" />
      <path d={waves(20, 180, 197, 16, 2)} data-id="element-749" data-mp="1zn63ipjt6215" />
    </>,
  /* ---------------- Portugal ---------------- */
  belem: (a, b) => {
    const dome = (x: number, y: number) => `M${x - 5} ${y} V${y - 8} Q${x} ${y - 16} ${x + 5} ${y - 8} V${y}`;
    return <>
        <path d="M44 182 V132 H156 V182" fill={a} data-id="element-750" data-mp="2nel8yr6vszgm" />
        <path d={crenels(44, 156, 132, 5)} data-id="element-751" data-mp="2ngege8pbjnpo" />
        <path d="M78 132 V52 H122 V132" fill={b} data-id="element-752" data-mp="2iep7ozpkjm4r" />
        <path d={crenels(78, 122, 52, 4)} data-id="element-753" data-mp="2ihbntnqiz9yl" />
        {[dome(44, 127), dome(156, 127), dome(78, 47), dome(122, 47)].map(d => <path key={d} d={d} data-id="element-754" data-mp="28pp390saryj6" />)}
        <path d={roundArch(100, 90, 80, 5)} data-id="element-755" data-mp="28ontczn0pi31" />
        <path d="M92 92 H108" data-id="element-756" data-mp="364t70uvq4zpy" />
        <path d={roundArch(90, 116, 110, 3)} data-id="element-757" data-mp="3652xejkol8w6" />
        <path d={roundArch(110, 116, 110, 3)} data-id="element-758" data-mp="366w6s6moi77v" />
        {[62, 80, 120, 138].map(x => <path key={x} d={roundArch(x, 170, 160, 5)} data-id="element-759" data-mp="31cs0zqp5g58d" />)}
        <path d={waves(0, 200, 188, 16, 3)} data-id="element-760" data-mp="2lv1t3et1xgc5" />
        <path d={waves(20, 180, 196, 16, 2)} data-id="element-761" data-mp="2ly61kodjuy8m" />
      </>;
  },
  tram28: (a, b) => <>
      <path d="M16 58 H184" data-id="element-762" data-mp="2qt3c5zqh9fnm" />
      <path d="M100 94 L86 72 L100 58" data-id="element-763" data-mp="2by5i92foki4f" />
      <path d="M30 172 V108 Q30 100 38 100 H162 Q170 100 170 108 V172 Z" fill={b} data-id="element-764" data-mp="2bz72nxil6gyt" />
      <path d="M30 150 H170 V172 H30 Z" fill={a} data-id="element-765" data-mp="2gy1mn2saibki" />
      <path d="M36 100 V94 H164 V100" data-id="element-766" data-mp="2gythuchz67a9" />
      {[40, 62, 84, 106, 128, 150].map(x => <path key={x} d={`M${x} 110 h14 v26 h-14 Z`} data-id="element-767" data-mp="34onscnetnjrc" />)}
      <path d="M92 156 h16 v8 h-16 Z" data-id="element-768" data-mp="39kcla7wc62eo" />
      <circle cx="58" cy="178" r="7" data-id="element-769" data-mp="39nibjqgpbabz" />
      <circle cx="142" cy="178" r="7" data-id="element-770" data-mp="2uxjfpucmcazo" />
      <path d="M8 186 H192 M8 190 H192" data-id="element-771" data-mp="2uwhskribvy1l" />
    </>,
  ponte25: a => <>
      <path d="M0 110 Q25 60 50 34 Q100 150 150 34 Q175 60 200 110" data-id="element-772" data-mp="2zujle45k3bq9" />
      {[60, 70, 80, 90, 100, 110, 120, 130, 140].map(x => <path key={x} d={`M${x} ${cableY(x)} V150`} strokeOpacity={0.6} data-id="element-773" data-mp="3njm1h7kjkwin" />)}
      {[50, 150].map(x => <path key={x} d={`M${x - 5} 190 V34 M${x + 5} 190 V34 M${x - 5} 34 H${x + 5} M${x - 5} 70 H${x + 5} M${x - 5} 110 H${x + 5}`} data-id="element-774" data-mp="1gx6if5udjqwo" />)}
      <path d="M0 150 H200 V156 H0 Z" fill={a} data-id="element-775" data-mp="1bxafl9xey5ip" />
      <path d={waves(0, 200, 178, 20, 3)} data-id="element-776" data-mp="1qmzvkba60e31" />
      <path d={waves(10, 190, 186, 20, 2)} data-id="element-777" data-mp="1qly8f8fvk14y" />
    </>
};